import React, { createContext, useContext, useState, useEffect } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';

const ThemeContext = createContext();

export const useCustomTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useCustomTheme must be used within a ThemeContextProvider');
  }
  return context;
};

// Backward compatibility
export const useTheme = useCustomTheme;

// Color theme options
export const colorThemes = {
  blue: {
    primary: { main: '#1976d2', light: '#42a5f5', dark: '#1565c0' },
    secondary: { main: '#dc004e', light: '#ff5983', dark: '#9a0036' },
    name: 'Blue Ocean'
  },
  purple: {
    primary: { main: '#9c27b0', light: '#ba68c8', dark: '#7b1fa2' },
    secondary: { main: '#ff9800', light: '#ffcc02', dark: '#f57c00' },
    name: 'Purple Haze'
  },
  green: {
    primary: { main: '#4caf50', light: '#81c784', dark: '#388e3c' },
    secondary: { main: '#ff5722', light: '#ff8a65', dark: '#d84315' },
    name: 'Nature Green'
  },
  teal: {
    primary: { main: '#009688', light: '#4db6ac', dark: '#00796b' },
    secondary: { main: '#e91e63', light: '#f06292', dark: '#c2185b' },
    name: 'Teal Wave'
  },
  orange: {
    primary: { main: '#ff9800', light: '#ffcc02', dark: '#f57c00' },
    secondary: { main: '#3f51b5', light: '#7986cb', dark: '#303f9f' },
    name: 'Sunset Orange'
  },
  indigo: {
    primary: { main: '#3f51b5', light: '#7986cb', dark: '#303f9f' },
    secondary: { main: '#4caf50', light: '#81c784', dark: '#388e3c' },
    name: 'Deep Indigo'
  },
  pink: {
    primary: { main: '#e91e63', light: '#f06292', dark: '#c2185b' },
    secondary: { main: '#00bcd4', light: '#4dd0e1', dark: '#0097a7' },
    name: 'Pink Blossom'
  },
  cyan: {
    primary: { main: '#00bcd4', light: '#4dd0e1', dark: '#0097a7' },
    secondary: { main: '#ff9800', light: '#ffcc02', dark: '#f57c00' },
    name: 'Cyan Sky'
  }
};

export const ThemeContextProvider = ({ children }) => {
  const [mode, setMode] = useState('light');
  const [colorTheme, setColorTheme] = useState('blue');

  // Load theme preferences from localStorage on initialization
  useEffect(() => {
    const savedMode = localStorage.getItem('edumetrix-theme-mode');
    const savedColorTheme = localStorage.getItem('edumetrix-color-theme');
    
    if (savedMode && (savedMode === 'light' || savedMode === 'dark')) {
      setMode(savedMode);
    }
    
    if (savedColorTheme && colorThemes[savedColorTheme]) {
      setColorTheme(savedColorTheme);
    }
  }, []);

  // Save theme preferences to localStorage
  const saveThemePreferences = (newMode, newColorTheme) => {
    localStorage.setItem('edumetrix-theme-mode', newMode);
    localStorage.setItem('edumetrix-color-theme', newColorTheme);
  };

  const toggleMode = () => {
    const newMode = mode === 'light' ? 'dark' : 'light';
    setMode(newMode);
    saveThemePreferences(newMode, colorTheme);
  };

  const changeColorTheme = (newColorTheme) => {
    if (colorThemes[newColorTheme]) {
      setColorTheme(newColorTheme);
      saveThemePreferences(mode, newColorTheme);
    }
  };

  // Create dynamic theme based on current settings
  const theme = createTheme({
    palette: {
      mode,
      primary: colorThemes[colorTheme].primary,
      secondary: colorThemes[colorTheme].secondary,
      ...(mode === 'dark' && {
        background: {
          default: '#121212',
          paper: '#1e1e1e',
        },
        text: {
          primary: '#d1d1d1ff',
          secondary: '#b3b3b3',
        },
      }),
      ...(mode === 'light' && {
        background: {
          default: '#fafafa',
          paper: '#ffffff',
        },
        text: {
          primary: '#000000',
          secondary: '#666666',
        },
      }),
    },
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: mode === 'dark' 
              ? '0 4px 20px rgba(0,0,0,0.3)' 
              : '0 4px 20px rgba(0,0,0,0.1)',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: mode === 'dark' 
                ? '0 8px 25px rgba(0,0,0,0.4)' 
                : '0 8px 25px rgba(0,0,0,0.15)',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: 'none',
            fontWeight: 600,
            padding: '8px 16px',
            transition: 'all 0.2s ease-in-out',
          },
          contained: {
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              transform: 'translateY(-1px)',
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            fontWeight: 500,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 12,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: mode === 'dark' 
              ? '0 2px 10px rgba(0,0,0,0.3)' 
              : '0 2px 10px rgba(0,0,0,0.1)',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRadius: '0 12px 12px 0',
          },
        },
      },
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: { fontWeight: 700, fontSize: '2.5rem' },
      h2: { fontWeight: 700, fontSize: '2rem' },
      h3: { fontWeight: 600, fontSize: '1.75rem' },
      h4: { fontWeight: 600, fontSize: '1.5rem' },
      h5: { fontWeight: 600, fontSize: '1.25rem' },
      h6: { fontWeight: 600, fontSize: '1.125rem' },
      body1: { fontSize: '1rem' },
      body2: { fontSize: '0.875rem' },
      button: { fontSize: '0.875rem', fontWeight: 600 },
      caption: { fontSize: '0.75rem' },
      overline: { fontSize: '0.75rem' },
    },
    shape: {
      borderRadius: 12,
    },
    transitions: {
      duration: {
        shortest: 150,
        shorter: 200,
        short: 250,
        standard: 300,
        complex: 375,
        enteringScreen: 225,
        leavingScreen: 195,
      },
    },
  });

  const contextValue = {
    mode,
    colorTheme,
    colorThemes,
    toggleMode,
    changeColorTheme,
    theme,
    isDark: mode === 'dark',
    isLight: mode === 'light',
    currentColorTheme: colorThemes[colorTheme],
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};